import {
  Button,
  DotsLoader,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { endOfDay, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getLocalizedDate } from '$shared/utils/date.utils';

import { getEventIconName } from '$features/monitoring/events/events.utils';

import { useRecentDeviations } from '../dashboard.api';
import type { RecentDeviation } from '../dashboard.types';

import { DashboardCardTitle } from './dashboard-card-title';
import { DashboardWidget } from './dashboard-widget';
import { SectionFeedback } from './section-feedback';
import { TruncatedText } from './truncated-text';

import './recent-deviations-card.scss';

function createMonitoringEventsSearch(deviation: RecentDeviation): string {
  const eventDate = new Date(deviation.timestamp);
  const searchParams = new URLSearchParams({
    sidecarRoute: 'monitoring/events',
    manufacturer: deviation.manufacturer,
    serialNumber: deviation.serialNumber,
    message: deviation.message,
    from: startOfDay(eventDate).getTime().toString(),
    to: endOfDay(eventDate).getTime().toString(),
  });

  return `?${searchParams.toString()}`;
}

function formatDeviationDuration(milliseconds: number): string {
  const totalMinutes = Math.floor(milliseconds / 60_000);
  if (totalMinutes < 1) return '< 1 min';

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(' ');
}

function DeviationDuration({ duration }: Pick<RecentDeviation, 'duration'>) {
  const { t } = useTranslation();

  if (duration.status === 'unknown') {
    return (
      <span className="recent-deviations-card__duration">
        {t('dashboard.recentDeviations.durationUnknown')}
      </span>
    );
  }

  return (
    <span
      className={`recent-deviations-card__duration recent-deviations-card__duration--${duration.status}`}
    >
      {t(
        duration.status === 'resolved'
          ? 'dashboard.recentDeviations.resolvedAfter'
          : 'dashboard.recentDeviations.ongoingFor',
        { duration: formatDeviationDuration(duration.milliseconds) },
      )}
    </span>
  );
}

export function RecentDeviationsCard() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useRecentDeviations();

  return (
    <DashboardWidget className="recent-deviations-card">
      <div className="dashboard-widget__header">
        <div className="dashboard-widget__title-row">
          <DashboardCardTitle
            icon="events"
            tone="red"
            title={t('dashboard.recentDeviations.title')}
            hint={t('dashboard.recentDeviations.hint')}
            subheadline={t('dashboard.recentDeviations.period')}
          />
          {data && <span className="recent-deviations-card__total">{data.total}</span>}
        </div>
        <SectionFeedback sectionId="recent-deviations" />
      </div>
      {isLoading && <DotsLoader className="m-top-l" />}
      {isError && (
        <div className="recent-deviations-card__feedback" role="alert">
          <span>{t('dashboard.recentDeviations.loadError')}</span>
          <Button
            variant="secondary"
            onClick={() => {
              refetch();
            }}
          >
            {t('dashboard.recentDeviations.retry')}
          </Button>
        </div>
      )}
      {!isLoading && !isError && data?.items.length === 0 && (
        <div className="recent-deviations-card__empty">
          {t('dashboard.recentDeviations.noDeviations')}
        </div>
      )}
      {!isLoading && !isError && data && data.items.length > 0 && (
        <div className="recent-deviations-card__list">
          {data.items.map((deviation) => (
            <Link
              key={deviation.id}
              className="recent-deviations-card__item"
              to={{
                search: createMonitoringEventsSearch(deviation),
              }}
            >
              <Icon
                name={`event-${getEventIconName(deviation.severity)}`}
                size={IconSizeClasses.XSmall}
              />
              <div className="recent-deviations-card__details">
                <TruncatedText
                  text={deviation.message}
                  className="recent-deviations-card__message"
                />
                <div className="recent-deviations-card__metadata">
                  <TruncatedText text={deviation.deviceName} />
                  {deviation.location && <span>{deviation.location}</span>}
                </div>
              </div>
              <div className="recent-deviations-card__timing">
                <time
                  className="recent-deviations-card__timestamp"
                  dateTime={deviation.timestamp}
                >
                  {getLocalizedDate(deviation.timestamp)}
                </time>
                <DeviationDuration duration={deviation.duration} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </DashboardWidget>
  );
}
